import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../../utils/api'

// Poll cadence for a running bulk job. The heavy work is server-side; we only
// read cheap counter rows, so a few seconds is plenty.
export const POLL_MS = 4000

const BASE = '/api/pricing/market-price/bulk-lookup'
const TERMINAL = new Set(['completed', 'failed', 'budget_exhausted'])

// A job is finished when it reaches one of these states — stop polling.
export function isTerminal(status) {
  return TERMINAL.has(status)
}

/**
 * Drives the bulk market-price lookup job from the Pricing Management page:
 * start a job for a set of products, poll its progress until it finishes, and
 * re-attach to a job still running after a reload / navigation.
 *
 * Returns { job, error, running, start, dismiss }:
 *  - job     — the latest status object { job_id, status, total, done, skipped,
 *              errors, pending, current_product_id? } or null.
 *  - error   — a message if starting failed (not a per-item lookup failure).
 *  - running — true while a non-terminal job is being tracked.
 *  - start(shopifyProductIds) — enqueue a job (or re-attach if one's in flight).
 *  - dismiss() — stop polling and clear the local view (job keeps running server-side).
 */
export function useBulkMarketPriceLookup() {
  const [job, setJob] = useState(null)
  const [error, setError] = useState(null)
  const pollRef = useRef(null)
  // Generation stamp: an in-flight HTTP response from a superseded start/mount
  // must not clobber the current job. Bump on every start and on mount.
  const genRef = useRef(0)

  const stopPolling = useCallback(() => {
    clearInterval(pollRef.current)
    pollRef.current = null
  }, [])

  // Fetch one status snapshot; drop it if a newer generation has taken over.
  const fetchStatus = useCallback(async (jobId, gen) => {
    const r = await api.get(`${BASE}/${jobId}`)
    if (genRef.current !== gen) return null
    setJob(r)
    if (isTerminal(r?.status)) stopPolling()
    return r
  }, [stopPolling])

  const startPolling = useCallback((jobId, gen) => {
    clearInterval(pollRef.current)
    pollRef.current = setInterval(() => {
      fetchStatus(jobId, gen).catch(() => { /* transient — keep polling */ })
    }, POLL_MS)
  }, [fetchStatus])

  const attach = useCallback((statusObj, gen) => {
    setJob(statusObj)
    if (statusObj?.job_id != null && !isTerminal(statusObj.status)) {
      startPolling(statusObj.job_id, gen)
    }
  }, [startPolling])

  // Re-attach on mount to any job still running.
  useEffect(() => {
    const gen = ++genRef.current
    api.get(`${BASE}/active`)
      .then((r) => {
        if (genRef.current !== gen) return
        if (r && r.job_id != null && !isTerminal(r.status)) attach(r, gen)
      })
      .catch(() => { /* no active job / transient */ })
    return () => clearInterval(pollRef.current)
  }, [attach])

  const start = useCallback(async (shopifyProductIds) => {
    setError(null)
    stopPolling() // never leave a prior job's interval firing when a new start begins
    const gen = ++genRef.current
    let created
    try {
      created = await api.post(BASE, { shopify_product_ids: shopifyProductIds })
    } catch (e) {
      // A job is already running — re-attach to it rather than surfacing an error.
      if (e?.status === 409) {
        try {
          const active = await api.get(`${BASE}/active`)
          if (genRef.current === gen && active && active.job_id != null) {
            attach(active, gen)
            return active
          }
        } catch { /* fall through to the generic message */ }
        setError('A bulk market-price lookup is already running.')
        return null
      }
      setError(e?.message || 'Failed to start bulk market-price lookup')
      throw e
    }
    // Normalize on the status shape (the create response omits status/done/errors).
    // The job is already running server-side, so if this first status read fails
    // (transient), fall back to a minimal job built from the create response and
    // let polling fill in the real numbers — otherwise a running job would be
    // invisible until the next page load re-attaches to it.
    let status = null
    try {
      const r = await api.get(`${BASE}/${created.job_id}`)
      if (genRef.current !== gen) return null
      status = r
    } catch {
      if (genRef.current !== gen) return null
    }
    if (!status || !status.status) {
      status = {
        job_id: created.job_id,
        status: 'running',
        total: created.total ?? 0,
        done: 0,
        skipped: created.skipped ?? 0,
        errors: 0,
        pending: created.to_process ?? 0,
      }
    }
    setJob(status)
    if (!isTerminal(status.status)) startPolling(created.job_id, gen)
    return status
  }, [stopPolling, startPolling, attach])

  const dismiss = useCallback(() => {
    genRef.current++ // orphan any in-flight response so it can't re-set job
    stopPolling()
    setJob(null)
    setError(null)
  }, [stopPolling])

  const running = job != null && !isTerminal(job.status)
  return { job, error, running, start, dismiss }
}
